import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { medicamentoRegistradoService } from '../services';
import { 
  ArrowLeft,
  Save,
  Trash2,
  Package,
  AlertTriangle,
  Eye,
  EyeOff
} from 'lucide-react';
import './EditarMedicamentoRegistrado.css';

const EditarMedicamentoRegistrado = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [medicamento, setMedicamento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dados do formulário
  const [formData, setFormData] = useState({
    nome: '',
    principioAtivo: '',
    codigoBarras: '',
    categoria: 'outros',
    tipoControle: 'sem_controle',
    formaFarmaceutica: '',
    dosagem: '',
    prescricaoObrigatoria: false
  });
  
  // Modal de confirmação
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(''); // 'edit' ou 'delete'
  const [senha, setSenha] = useState('');
  const [showSenha, setShowSenha] = useState(false);
  
  const [categorias, setCategorias] = useState([]);

  // Verificar permissão
  useEffect(() => {
    if (!['admin', 'farmaceutico'].includes(user?.cargo)) {
      navigate('/medicamentos-registrados');
      return;
    }
  }, [user, navigate]);

  // Carregar categorias
  useEffect(() => {
    carregarCategorias();
  }, []);

  // Carregar medicamento
  useEffect(() => {
    if (id) {
      carregarMedicamento();
    }
  }, [id]);

  const carregarCategorias = async () => {
    try {
      const categoriasData = await medicamentoRegistradoService.getCategorias();
      setCategorias(categoriasData);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const carregarMedicamento = async () => {
    try {
      setLoading(true);
      const data = await medicamentoRegistradoService.getById(id);
      setMedicamento(data);
      
      // Preencher formulário
      setFormData({
        nome: data.nome || '',
        principioAtivo: data.principioAtivo || '',
        codigoBarras: data.codigoBarras || '',
        categoria: data.categoria || 'outros',
        tipoControle: data.tipoControle || 'sem_controle',
        formaFarmaceutica: data.formaFarmaceutica || '',
        dosagem: data.dosagem || '',
        prescricaoObrigatoria: data.prescricaoObrigatoria || false
      });
    } catch (error) {
      console.error('Erro ao carregar medicamento:', error);
      setError('Erro ao carregar medicamento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (campo, valor) => {
    setFormData(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome.trim() || !formData.principioAtivo.trim()) {
      setError('Nome e princípio ativo são obrigatórios');
      return;
    }

    setConfirmAction('edit');
    setShowConfirmModal(true);
  };

  const handleDelete = () => {
    setConfirmAction('delete');
    setShowConfirmModal(true);
  };

  const confirmarAcao = async () => {
    if (!senha.trim()) {
      setError('Senha é obrigatória para confirmar a ação');
      return;
    }

    try {
      setSalvando(true);
      setError('');

      if (confirmAction === 'edit') {
        await medicamentoRegistradoService.adminEdit(id, formData, senha);
        setSuccess('Medicamento atualizado com sucesso!');
        setTimeout(() => {
          navigate('/medicamentos-registrados');
        }, 2000);
      } else if (confirmAction === 'delete') {
        await medicamentoRegistradoService.adminDelete(id, senha);
        setSuccess('Medicamento removido com sucesso!');
        setTimeout(() => {
          navigate('/medicamentos-registrados');
        }, 2000);
      }
      
      setShowConfirmModal(false);
      setSenha('');
    } catch (error) {
      console.error('Erro na ação:', error);
      setError(error.response?.data?.message || 'Erro ao executar ação. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const cancelarConfirmacao = () => {
    setShowConfirmModal(false);
    setConfirmAction('');
    setSenha('');
    setError('');
  };

  const formatarCategoria = (categoria) => {
    const categoriaMap = {
      'analgesico': 'Analgésico',
      'antibiotico': 'Antibiótico',
      'antinflamatorio': 'Anti-inflamatório',
      'cardiovascular': 'Cardiovascular',
      'dermatologico': 'Dermatológico',
      'gastrointestinal': 'Gastrointestinal',
      'neurologico': 'Neurológico',
      'psiquiatrico': 'Psiquiátrico',
      'respiratorio': 'Respiratório',
      'vitaminas': 'Vitaminas',
      'outros': 'Outros'
    };
    return categoriaMap[categoria] || categoria;
  };

  if (loading) {
    return (
      <div className="editar-medicamento-container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Carregando medicamento...</p>
        </div>
      </div>
    );
  }

  if (!medicamento) {
    return (
      <div className="editar-medicamento-container">
        <div className="error-state">
          <h2>Medicamento não encontrado</h2>
          <button onClick={() => navigate('/medicamentos-registrados')}>
            Voltar à lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="editar-medicamento-container">
      <div className="page-header">
        <div className="header-content">
          <button 
            className="back-button"
            onClick={() => navigate('/medicamentos-registrados')}
          >
            <ArrowLeft size={20} />
            Voltar
          </button>
          
          <div className="header-info">
            <Package size={32} className="header-icon" />
            <div>
              <h1>Editar Medicamento Registrado</h1>
              <p>Modificar informações do medicamento</p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <AlertTriangle size={20} />
          {error}
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      <div className="content-wrapper">
        <form onSubmit={handleSubmit} className="medicamento-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="nome">Nome do Medicamento *</label>
              <input
                type="text"
                id="nome"
                value={formData.nome}
                onChange={(e) => handleInputChange('nome', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="principioAtivo">Princípio Ativo *</label>
              <input
                type="text"
                id="principioAtivo"
                value={formData.principioAtivo}
                onChange={(e) => handleInputChange('principioAtivo', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="dosagem">Dosagem</label>
              <input
                type="text"
                id="dosagem"
                placeholder="Ex: 500mg, 10ml/dose"
                value={formData.dosagem}
                onChange={(e) => handleInputChange('dosagem', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="codigoBarras">Código de Barras</label>
              <input
                type="text"
                id="codigoBarras"
                value={formData.codigoBarras}
                onChange={(e) => handleInputChange('codigoBarras', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="categoria">Categoria</label>
              <select
                id="categoria"
                value={formData.categoria}
                onChange={(e) => handleInputChange('categoria', e.target.value)}
              >
                {categorias.map(categoria => (
                  <option key={categoria} value={categoria}>
                    {formatarCategoria(categoria)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="tipoControle">Tipo de Controle</label>
              <select
                id="tipoControle"
                value={formData.tipoControle}
                onChange={(e) => handleInputChange('tipoControle', e.target.value)}
              >
                <option value="sem_controle">Sem Controle</option>
                <option value="normal">Tarja Vermelha</option>
                <option value="especial">Tarja Preta</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="formaFarmaceutica">Forma Farmacêutica</label>
              <input
                type="text"
                id="formaFarmaceutica"
                placeholder="Ex: Comprimido, Cápsula, Xarope"
                value={formData.formaFarmaceutica}
                onChange={(e) => handleInputChange('formaFarmaceutica', e.target.value)}
              />
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.prescricaoObrigatoria}
                  onChange={(e) => handleInputChange('prescricaoObrigatoria', e.target.checked)}
                />
                Prescrição Obrigatória
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="delete-btn"
              onClick={handleDelete}
              disabled={salvando}
            >
              <Trash2 size={20} />
              Remover Medicamento
            </button>

            <div className="action-group">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => navigate('/medicamentos-registrados')}
                disabled={salvando}
              >
                Cancelar
              </button>
              
              <button
                type="submit"
                className="save-btn"
                disabled={salvando}
              >
                <Save size={20} />
                {salvando ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Modal de Confirmação */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="confirm-modal">
            <div className="modal-header">
              <AlertTriangle size={24} className="warning-icon" />
              <h3>
                {confirmAction === 'edit' ? 'Confirmar Edição' : 'Confirmar Remoção'}
              </h3>
            </div>

            <div className="modal-body">
              <p>
                {confirmAction === 'edit' 
                  ? 'Para confirmar a edição deste medicamento, digite sua senha de login:'
                  : 'Esta ação removerá permanentemente o medicamento do sistema. Digite sua senha de login para confirmar:'
                }
              </p>

              <div className="password-input-group">
                <input
                  type={showSenha ? 'text' : 'password'}
                  placeholder="Digite sua senha de login"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  className="toggle-password-btn"
                  onClick={() => setShowSenha(!showSenha)}
                >
                  {showSenha ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={cancelarConfirmacao}
                disabled={salvando}
              >
                Cancelar
              </button>
              
              <button
                type="button"
                className={`confirm-btn ${confirmAction === 'delete' ? 'delete' : 'edit'}`}
                onClick={confirmarAcao}
                disabled={salvando || !senha.trim()}
              >
                {salvando ? 'Processando...' : 
                 confirmAction === 'edit' ? 'Confirmar Edição' : 'Confirmar Remoção'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditarMedicamentoRegistrado;